import React, { useState } from 'react';
import {
  Box,
  Heading,
  VStack,
  HStack,
  Text,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  Flex,
  Divider,
  useColorModeValue,
  Progress,
  Select,
  Card,
  CardHeader,
  CardBody,
  Tooltip,
  Button,
} from '@chakra-ui/react';
import { 
  FiDollarSign,
  FiUsers,
  FiTrendingUp,
  FiTrendingDown,
  FiActivity,
  FiAlertTriangle,
  FiCheckCircle
} from 'react-icons/fi';
import { useUserContext } from '../../../context/UserContext';
import { useGetPlanInfo } from '../../../services/planService';
import { formatCurrency, getCurrencySymbol } from '../../../utils/currency';
import { useCalculateFundingRequirement } from '../../../services/payrollService';

const FundingCalculator = () => {
  const { user } = useUserContext();
  
  // Get plan info for country-based currency
  const { planInfo } = useGetPlanInfo();
  
  // Removed selectedPaymentType state - no longer needed with simplified backend
  
  // State for month/year selection
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // API hooks with month/year parameters
  const { 
    data: fundingRequirement, 
    isLoading: fundingRequirementIsLoading, 
    error: fundingRequirementError,
    refetch: refetchFundingRequirement 
  } = useCalculateFundingRequirement({
    month: selectedMonth,
    year: selectedYear,
    payment_type: 'both'
  });

  // Theme colors
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const cardBg = useColorModeValue("gray.50", "gray.700");

  // Use the centralized currency utility functions
  // The formatCurrency function is already imported from utils/currency

  if (fundingRequirementIsLoading) {
    return (
      <Box p={6} bg={bgColor} borderRadius="lg" border="1px" borderColor={borderColor}>
        <Flex justify="center" align="center" h="200px">
          <Spinner size="xl" />
        </Flex>
      </Box>
    );
  }

  if (fundingRequirementError) {
    return (
      <Box p={6} bg={bgColor} borderRadius="lg" border="1px" borderColor={borderColor}>
        <Alert status="error">
          <AlertIcon />
          <Box>
            <AlertTitle>Error loading funding requirements!</AlertTitle>
            <AlertDescription>
              {fundingRequirementError}
            </AlertDescription>
          </Box>
        </Alert>
      </Box>
    );
  }

  // Process the API response structure
  const employeeDetails = fundingRequirement?.employee_details || [];
  const paymentOptions = fundingRequirement?.payment_options;
  const payrollSummary = fundingRequirement?.payroll_summary;
  const fundingReq = fundingRequirement?.funding_requirement;
  const feeBreakdown = fundingRequirement?.fee_breakdown;
  const availableBalance = fundingRequirement?.available_balance ?? fundingRequirement?.wallet_balance;
  const pendingSettlement = fundingRequirement?.pending_settlement ?? 0;
  const currency = fundingRequirement?.currency || 'USD';
  const period = fundingRequirement?.period;

  return (
    <Box p={6} bg={bgColor} borderRadius="lg" border="1px" borderColor={borderColor}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <VStack align="start" spacing={4}>
          <VStack align="start" spacing={2}>
            <Heading size="lg" color="blue.600">
              Funding Calculator
            </Heading>
            <Text color="gray.600">
              Calculate the total funding requirement for your payroll including all fees and deductions
            </Text>
          </VStack>
          
        </VStack>


        {/* Debug Information */}
        {process.env.NODE_ENV === 'development' && (
          <Box p={4} bg="gray.100" borderRadius="md" fontSize="sm">
            <Text fontWeight="bold" mb={2}>Debug Info:</Text>
            <Text>Period: {period || 'N/A'}</Text>
            <Text>Total Employees: {employeeDetails?.length || 'N/A'}</Text>
            <Text>Available Balance: {availableBalance || 'N/A'}</Text>
            <Text>Pending Settlement: {pendingSettlement || 'N/A'}</Text>
            <Text>Funding Needed: {fundingReq?.funding_needed || 'N/A'}</Text>
            <Text>Currency: {currency || 'N/A'}</Text>
            <Text>Payment Options Available: {paymentOptions ? 'Yes' : 'No'}</Text>
          </Box>
        )}

        <Divider />

        {/* Wallet Status */}
        <Card>
          <CardHeader pb={2}>
            <VStack align="start" spacing={2}>
              <Heading size="md" color="gray.700">Current Wallet Status</Heading>
              <Text fontSize="sm" color="gray.600">
                Your company wallet balance and funding requirements
              </Text>
              <Tooltip 
                label="This shows your current wallet balance, how much funding you need for payroll, and whether you have sufficient funds to process payments."
                placement="top"
                hasArrow
              >
                <Badge colorScheme="blue" variant="subtle" cursor="help">
                  💰 Balance & Requirements
                </Badge>
              </Tooltip>
            </VStack>
          </CardHeader>
          <CardBody pt={0}>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <Box textAlign="center">
                <Text fontSize="sm" color="gray.600">Available Balance</Text>
                <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                  {formatCurrency(availableBalance || 0, planInfo?.country)}
                </Text>
                <Text fontSize="xs" color="gray.500">Available for payroll</Text>
              </Box>
              <Box textAlign="center">
                <Text fontSize="sm" color="gray.600">Funding Needed</Text>
                <Text fontSize="2xl" fontWeight="bold" color="red.600">
                  {formatCurrency(fundingReq?.funding_needed || 0, planInfo?.country)}
                </Text>
                <Text fontSize="xs" color="gray.500">To process payroll</Text>
              </Box>
              <Box textAlign="center">
                <Text fontSize="sm" color="gray.600">Status</Text>
                <Badge 
                  colorScheme={fundingReq?.sufficient_funds ? "green" : "red"} 
                  fontSize="md" 
                  p={2}
                >
                  {fundingReq?.sufficient_funds ? "Sufficient Funds" : "Insufficient Funds"}
                </Badge>
                <Text fontSize="xs" color="gray.500">
                  {fundingReq?.sufficient_funds ? "Ready to pay" : "Need to fund"}
                </Text>
              </Box>
            </SimpleGrid>
            {pendingSettlement > 0 && (
              <Box mt={4} p={3} bg="orange.50" borderRadius="md" border="1px solid" borderColor="orange.200">
                <HStack justify="space-between">
                  <VStack align="start" spacing={0}>
                    <Text fontSize="sm" fontWeight="600" color="orange.700">
                      Pending Settlement
                    </Text>
                    <Text fontSize="xs" color="orange.600">
                      Not available for use yet
                    </Text>
                  </VStack>
                  <Text fontSize="lg" fontWeight="700" color="orange.800">
                    {formatCurrency(pendingSettlement, planInfo?.country)}
                  </Text>
                </HStack>
              </Box>
            )}
          </CardBody>
        </Card>

        {/* Fee Configuration */}
        <Card>
          <CardHeader pb={2}>
            <VStack align="start" spacing={2}>
              <Heading size="md" color="gray.700">Fee Configuration</Heading>
              <Text fontSize="sm" color="gray.600">
                Payment processing fees and tax rates applied to payroll
              </Text>
              <Tooltip 
                label="These are the fees and tax rates that will be applied to your payroll processing. Transfer charges are for payment processing, processing fees are for our service, and tax rates are based on your country's regulations."
                placement="top"
                hasArrow
              >
                <Badge colorScheme="purple" variant="subtle" cursor="help">
                  ⚙️ Fees & Taxes
                </Badge>
              </Tooltip>
            </VStack>
          </CardHeader>
          <CardBody pt={0}>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <Box textAlign="center">
                <Text fontSize="sm" color="gray.600">Transfer Charges</Text>
                <Text fontSize="xl" fontWeight="bold" color="purple.600">
                  Per Transaction
                </Text>
                <Text fontSize="xs" color="gray.500">Payment processing</Text>
              </Box>
              <Box textAlign="center">
                <Text fontSize="sm" color="gray.600">Processing Fee</Text>
                <Text fontSize="xl" fontWeight="bold" color="teal.600">
                  {feeBreakdown?.processing_fee_percentage || 0}%
                </Text>
                <Text fontSize="xs" color="gray.500">Service fee</Text>
              </Box>
              <Box textAlign="center">
                <Text fontSize="sm" color="gray.600">Default Tax Rate</Text>
                <Text fontSize="xl" fontWeight="bold" color="red.600">
                  {feeBreakdown?.default_tax_percentage || 0}%
                </Text>
                <Text fontSize="xs" color="gray.500">Government tax</Text>
              </Box>
            </SimpleGrid>
          </CardBody>
        </Card>

        {/* No Data State */}
        {employeeDetails.length === 0 && (
          <Alert status="info">
            <AlertIcon />
            <Box>
              <AlertTitle>No Payroll Data Available</AlertTitle>
              <AlertDescription>
                There are no employees with payroll data for the current period. 
                Please ensure employees have been added to payroll batches or have salary information configured.
              </AlertDescription>
            </Box>
          </Alert>
        )}

        {/* Simple Payment Options - Real Data Only */}
        {(paymentOptions || payrollSummary) && (
          <SimpleGrid columns={{ base: 1, md: paymentOptions ? 2 : 1 }} spacing={6}>
            {/* Regular Pay Card - Show if available */}
            {(paymentOptions?.regular_pay || (payrollSummary && fundingRequirement?.payment_type === 'regular_pay')) && (
              <Card borderColor="blue.200" borderWidth="2px">
                <CardHeader pb={2}>
                  <VStack align="start" spacing={2}>
                    <Heading size="md" color="blue.600">Regular Pay</Heading>
                    <Text fontSize="sm" color="gray.600">
                      Fixed monthly salary payment
                    </Text>
                    <Tooltip 
                      label="Regular pay is the standard monthly salary that employees receive regardless of attendance. This is the most predictable payment method with consistent costs throughout the month."
                      placement="top"
                      hasArrow
                    >
                      <Badge colorScheme="blue" variant="subtle" cursor="help">
                        💡 Predictable & Consistent
                      </Badge>
                    </Tooltip>
                  </VStack>
                </CardHeader>
                <CardBody pt={0}>
                  <VStack align="stretch" spacing={3}>
                    <HStack justify="space-between">
                      <Text fontSize="sm" color="gray.600">Net Salaries:</Text>
                      <Text fontWeight="bold" color="green.600">
                        {formatCurrency(
                          paymentOptions?.regular_pay?.summary?.total_net_amount || 
                          payrollSummary?.total_net_amount, 
                          planInfo?.country
                        )}
                      </Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="sm" color="gray.600">Transfer Charges:</Text>
                      <Text fontWeight="bold" color="purple.600">
                        {formatCurrency(
                          paymentOptions?.regular_pay?.summary?.total_flutterwave_fees || 0,
                          planInfo?.country
                        )}
                      </Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="sm" color="gray.600">Processing Fee ({feeBreakdown?.processing_fee_percentage || 0}%):</Text>
                      <Text fontWeight="bold" color="teal.600">
                        {formatCurrency(
                          paymentOptions?.regular_pay?.summary?.total_processing_fees || 0,
                          planInfo?.country
                        )}
                      </Text>
                    </HStack>
                    <Divider />
                    <HStack justify="space-between">
                      <Text fontSize="sm" color="gray.600" fontWeight="medium">Total Company Cost:</Text>
                      <Text fontWeight="bold" fontSize="md" color="red.600">
                        {formatCurrency(
                          paymentOptions?.regular_pay?.summary?.total_company_cost || 
                          payrollSummary?.total_company_cost, 
                          planInfo?.country
                        )}
                      </Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="sm" color="gray.600" fontWeight="medium">Funding Needed:</Text>
                      <Text fontWeight="bold" fontSize="md" color="orange.600">
                        {formatCurrency(
                          paymentOptions?.regular_pay?.funding_requirement?.funding_needed || 
                          fundingReq?.funding_needed, 
                          planInfo?.country
                        )}
                      </Text>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>
            )}

            {/* Total Pay Card - Show if available */}
            {(paymentOptions?.total_pay || (payrollSummary && fundingRequirement?.payment_type === 'total_pay')) && (
              <Card borderColor="green.200" borderWidth="2px">
                <CardHeader pb={2}>
                  <VStack align="start" spacing={2}>
                    <Heading size="md" color="green.600">Total Pay</Heading>
                    <Text fontSize="sm" color="gray.600">
                      Hours-based payment with real attendance data
                    </Text>
                    <Tooltip 
                      label="Total pay calculates salaries based on actual hours worked and attendance. This shows real data only - no predictions or projections."
                      placement="top"
                      hasArrow
                    >
                      <Badge colorScheme="green" variant="subtle" cursor="help">
                        ✅ Real Data Only
                      </Badge>
                    </Tooltip>
                  </VStack>
                </CardHeader>
                <CardBody pt={0}>
                  <VStack align="stretch" spacing={3}>
                    <HStack justify="space-between">
                      <Text fontSize="sm" color="gray.600">Net Salaries:</Text>
                      <Text fontWeight="bold" color="green.600">
                        {formatCurrency(
                          paymentOptions?.total_pay?.summary?.total_net_amount || 
                          payrollSummary?.total_net_amount, 
                          planInfo?.country
                        )}
                      </Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="sm" color="gray.600">Transfer Charges:</Text>
                      <Text fontWeight="bold" color="purple.600">
                        {formatCurrency(
                          paymentOptions?.total_pay?.summary?.total_flutterwave_fees || 0,
                          planInfo?.country
                        )}
                      </Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="sm" color="gray.600">Processing Fee ({feeBreakdown?.processing_fee_percentage || 0}%):</Text>
                      <Text fontWeight="bold" color="teal.600">
                        {formatCurrency(
                          paymentOptions?.total_pay?.summary?.total_processing_fees || 0,
                          planInfo?.country
                        )}
                      </Text>
                    </HStack>
                    <Divider />
                    <HStack justify="space-between">
                      <Text fontSize="sm" color="gray.600" fontWeight="medium">Total Company Cost:</Text>
                      <Text fontWeight="bold" fontSize="md" color="red.600">
                        {formatCurrency(
                          paymentOptions?.total_pay?.summary?.total_company_cost || 
                          payrollSummary?.total_company_cost, 
                          planInfo?.country
                        )}
                      </Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="sm" color="gray.600" fontWeight="medium">Funding Needed:</Text>
                      <Text fontWeight="bold" fontSize="md" color="orange.600">
                        {formatCurrency(
                          paymentOptions?.total_pay?.funding_requirement?.funding_needed || 
                          fundingReq?.funding_needed, 
                          planInfo?.country
                        )}
                      </Text>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>
            )}
          </SimpleGrid>
        )}

        {/* Employee Summary */}
        {employeeDetails.length > 0 && (
          <Card>
            <CardHeader pb={2}>
              <VStack align="start" spacing={2}>
                <Heading size="md" color="gray.700">Employee Summary</Heading>
                <Text fontSize="sm" color="gray.600">
                  Overview of employee payroll data and recommendations
                </Text>
                <Tooltip 
                  label="This section provides a summary of all employees in your payroll, their total salaries, deductions, and our recommendation for the most cost-effective payment method."
                  placement="top"
                  hasArrow
                >
                  <Badge colorScheme="teal" variant="subtle" cursor="help">
                    👥 Employee Overview
                  </Badge>
                </Tooltip>
              </VStack>
            </CardHeader>
            <CardBody pt={0}>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                <Box p={4} bg={cardBg} borderRadius="md">
                  <Stat>
                    <StatLabel>Total Employees</StatLabel>
                    <StatNumber color="blue.600">
                      <HStack>
                        <FiUsers />
                        <Text>{employeeDetails.length}</Text>
                      </HStack>
                    </StatNumber>
                    <StatHelpText>
                      <StatArrow type="increase" />
                      Active in payroll
                    </StatHelpText>
                  </Stat>
                </Box>

                <Box p={4} bg={cardBg} borderRadius="md">
                  <Stat>
                    <StatLabel>Total Base Salaries</StatLabel>
                    <StatNumber color="green.600">
                      <HStack>
                        <FiDollarSign />
                        <Text>{formatCurrency(employeeDetails.reduce((sum, emp) => sum + (emp.base_salary || 0), 0), planInfo?.country)}</Text>
                      </HStack>
                    </StatNumber>
                    <StatHelpText>
                      <StatArrow type="increase" />
                      Before deductions
                    </StatHelpText>
                  </Stat>
                </Box>

                <Box p={4} bg={cardBg} borderRadius="md">
                  <Stat>
                    <StatLabel>Total Deductions</StatLabel>
                    <StatNumber color="orange.600">
                      <HStack>
                        <FiDollarSign />
                        <Text>{formatCurrency(employeeDetails.reduce((sum, emp) => sum + (emp.deduction_breakdown?.reduce((dSum, ded) => dSum + (ded.amount || 0), 0) || 0), 0), planInfo?.country)}</Text>
                      </HStack>
                    </StatNumber>
                    <StatHelpText>
                      <StatArrow type="decrease" />
                      Taxes & deductions
                    </StatHelpText>
                  </Stat>
                </Box>

                <Box p={4} bg={cardBg} borderRadius="md">
                  <Stat>
                    <StatLabel>Recommended Payment</StatLabel>
                    <StatNumber color="purple.600">
                      <HStack>
                        <FiActivity />
                        <Text>{fundingReq?.recommended_payment_type || 'Regular Pay'}</Text>
                      </HStack>
                    </StatNumber>
                    <StatHelpText>
                      <StatArrow type="increase" />
                      Cost-effective option
                    </StatHelpText>
                  </Stat>
                </Box>
              </SimpleGrid>
            </CardBody>
          </Card>
        )}


        {/* Employee Details Table */}
        {employeeDetails.length > 0 && (
          <Card>
            <CardHeader pb={2}>
              <VStack align="start" spacing={2}>
                <Heading size="md" color="gray.700">Employee Details</Heading>
                <Text fontSize="sm" color="gray.600">
                  Detailed breakdown of each employee's payroll calculation
                </Text>
                <Tooltip 
                  label="This table shows the detailed payroll calculation for each employee, including their base salary, tax percentage, deductions, net pay amounts for both regular and total pay methods, and their attendance hours."
                  placement="top"
                  hasArrow
                >
                  <Badge colorScheme="orange" variant="subtle" cursor="help">
                    📊 Detailed Breakdown
                  </Badge>
                </Tooltip>
              </VStack>
            </CardHeader>
            <CardBody pt={0}>
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th>Employee</Th>
                      <Th>Base Salary</Th>
                      <Th>Tax %</Th>
                      <Th>Tax Amount</Th>
                      <Th>Deductions</Th>
                      <Th>Bonus</Th>
                      <Th>Leave Allowance</Th>
                      <Th>Regular Pay Net</Th>
                      <Th>Transfer Charge (Regular)</Th>
                      <Th>Processing Fee (Regular)</Th>
                      <Th>Total Pay Net</Th>
                      <Th>Transfer Charge (Total)</Th>
                      <Th>Processing Fee (Total)</Th>
                      <Th>Hours Worked</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {employeeDetails.map((employee, index) => (
                      <Tr key={index}>
                        <Td>
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="medium">{employee.employee_name}</Text>
                            <Text fontSize="sm" color="gray.600">
                              ID: {employee.employee_id}
                            </Text>
                          </VStack>
                        </Td>
                        <Td color="green.600" fontWeight="medium">
                          {formatCurrency(employee.base_salary, planInfo?.country)}
                        </Td>
                        <Td color="red.600">
                          {employee.tax_percentage}%
                        </Td>
                        <Td color="red.600" fontWeight="500">
                          {formatCurrency(employee.regular_pay?.tax_amount || 0, planInfo?.country)}
                        </Td>
                        <Td color="orange.600">
                          {formatCurrency(employee.deduction_breakdown?.reduce((sum, ded) => sum + (ded.amount || 0), 0) || 0, planInfo?.country)}
                        </Td>
                        <Td color="blue.600" fontWeight="500">
                          {formatCurrency(employee.total_pay?.bonus || 0, planInfo?.country)}
                        </Td>
                        <Td color="green.600" fontWeight="500">
                          {formatCurrency(employee.total_pay?.leave_allowance || 0, planInfo?.country)}
                        </Td>
                        <Td color="blue.600" fontWeight="medium">
                          {formatCurrency(employee.regular_pay?.net_amount || 0, planInfo?.country)}
                        </Td>
                        <Td color="purple.600" fontSize="sm">
                          {formatCurrency(employee.regular_pay?.flutterwave_fee || 0, planInfo?.country)}
                        </Td>
                        <Td color="teal.600" fontSize="sm" fontWeight="500">
                          {formatCurrency(employee.regular_pay?.processing_fee || 0, planInfo?.country)}
                        </Td>
                        <Td color="purple.600" fontWeight="medium">
                          {formatCurrency(employee.total_pay?.net_amount || 0, planInfo?.country)}
                        </Td>
                        <Td color="purple.600" fontSize="sm">
                          {formatCurrency(employee.total_pay?.flutterwave_fee || 0, planInfo?.country)}
                        </Td>
                        <Td color="teal.600" fontSize="sm" fontWeight="500">
                          {formatCurrency(employee.total_pay?.processing_fee || 0, planInfo?.country)}
                        </Td>
                        <Td>
                          <VStack align="start" spacing={0}>
                            <Text fontSize="sm" color="green.600">
                              Worked: {employee.attendance_summary?.total_hours_worked?.toFixed(2) || 0}h
                            </Text>
                            <Text fontSize="sm" color="red.600">
                              Missed: {employee.attendance_summary?.missed_hours?.toFixed(2) || 0}h
                            </Text>
                          </VStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </CardBody>
          </Card>
        )}

        {/* Deduction Details */}
        {employeeDetails.length > 0 && employeeDetails[0]?.deduction_breakdown && (
          <Card>
            <CardHeader pb={2}>
              <VStack align="start" spacing={2}>
                <Heading size="md" color="gray.700">
                  Deduction Breakdown (Sample Employee: {employeeDetails[0].employee_name})
                </Heading>
                <Text fontSize="sm" color="gray.600">
                  Example of how deductions are calculated for employees
                </Text>
                <Tooltip 
                  label="This shows a sample employee's deduction breakdown to help you understand how taxes and other deductions are calculated. Each deduction can be either a percentage of salary or a fixed amount."
                  placement="top"
                  hasArrow
                >
                  <Badge colorScheme="red" variant="subtle" cursor="help">
                    💸 Deduction Example
                  </Badge>
                </Tooltip>
              </VStack>
            </CardHeader>
            <CardBody pt={0}>
              <VStack align="stretch" spacing={2}>
                {employeeDetails[0].deduction_breakdown.map((deduction, index) => (
                  <HStack key={index} justify="space-between" p={2} bg="white" borderRadius="md">
                    <VStack align="start" spacing={0}>
                      <Text fontWeight="medium">{deduction.name}</Text>
                      <Badge colorScheme={deduction.type === 'PERCENTAGE' ? 'blue' : 'green'} size="sm">
                        {deduction.type} ({deduction.rate}%)
                      </Badge>
                    </VStack>
                    <Text fontWeight="bold" color="orange.600">
                      {formatCurrency(deduction.amount, planInfo?.country)}
                    </Text>
                  </HStack>
                ))}
              </VStack>
            </CardBody>
          </Card>
        )}

        {/* Funding Recommendation */}
        {fundingReq && (
          <Alert status={fundingReq.sufficient_funds ? "success" : "warning"}>
            <AlertIcon />
            <Box>
              <AlertTitle>
                {fundingReq.sufficient_funds ? "Sufficient Funds Available" : "Insufficient Funds"}
              </AlertTitle>
              <AlertDescription>
                {fundingReq.sufficient_funds ? (
                  <>
                    Your available balance of{' '}
                    <Text as="span" fontWeight="bold" color="green.600">
                      {formatCurrency(availableBalance || 0, planInfo?.country)}
                    </Text>{' '}
                    is sufficient to process this payroll.
                  </>
                ) : (
                  <>
                You need to fund your wallet with at least{' '}
                    <Text as="span" fontWeight="bold" color="red.600">
                      {formatCurrency(fundingReq.funding_needed, planInfo?.country)}
                </Text>{' '}
                    to process this payroll. Available balance: {formatCurrency(availableBalance || 0, planInfo?.country)}
                  </>
                )}
              </AlertDescription>
            </Box>
          </Alert>
        )}
      </VStack>
    </Box>
  );
};

export default FundingCalculator;